import { useEffect, useMemo, useRef, useState } from "react";
import { AppSwitcher } from "./AppSwitcher";
import { Icon } from "./Icon";
import type { DashboardAccountConfig, DashboardAccountMenuItem, DashboardLogo, DashboardShellConfig } from "./types";

export function formatAlertCount(count?: number) {
  if (!count || count < 1) return null;
  return count > 9 ? "9+" : String(Math.floor(count));
}

function renderLogo(logo: DashboardLogo | undefined, fallbackAlt: string) {
  if (!logo) return null;
  if (typeof logo === "object" && "src" in logo) {
    return <img src={logo.src} alt={logo.alt || fallbackAlt} />;
  }
  return logo;
}

function AccountMenuItem({
  item,
  onDone,
  onNavigate,
}: {
  item: DashboardAccountMenuItem;
  onDone: () => void;
  onNavigate: (route: string) => void;
}) {
  if (item.visible === false) return null;

  const handleClick = () => {
    if (item.disabled) return;
    onDone();
    item.onSelect?.();
    if (item.route) onNavigate(item.route);
  };

  return (
    <button
      type="button"
      className={[
        "in-app-profile-menu-item",
        item.danger ? "danger" : "",
        item.disabled ? "disabled" : "",
      ].join(" ")}
      onClick={handleClick}
      disabled={item.disabled}
      role="menuitem"
    >
      {item.icon ? <Icon name={item.icon} className="in-app-profile-menu-icon" /> : null}
      <span>{item.label}</span>
    </button>
  );
}

export type TopNavProps = {
  config: DashboardShellConfig;
  expanded: boolean;
  account: DashboardAccountConfig;
  alertCount?: number;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  onToggleNav: () => void;
  onNavigate: (route: string) => void;
  onSearch?: (query: string) => void;
  onAlertsClick?: () => void;
};

export function TopNav({
  config,
  expanded,
  account,
  alertCount,
  darkMode,
  onToggleDarkMode,
  onToggleNav,
  onNavigate,
  onSearch,
  onAlertsClick,
}: TopNavProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const menuItems = useMemo(() => {
    const visibleItems = account.menuItems?.filter((item) => item.visible !== false) || [];
    if (visibleItems.some((item) => item.id === "dark-mode")) return visibleItems;

    const darkModeItem: DashboardAccountMenuItem = {
      id: "dark-mode",
      label: darkMode ? "Light mode" : "Dark mode",
      icon: "bedtime",
      onSelect: onToggleDarkMode,
    };
    const signOutIndex = visibleItems.findIndex((item) => item.id === "sign-out" || item.danger);

    if (signOutIndex === -1) return [...visibleItems, darkModeItem];
    return [...visibleItems.slice(0, signOutIndex), darkModeItem, ...visibleItems.slice(signOutIndex)];
  }, [account.menuItems, darkMode, onToggleDarkMode]);
  const alertBadge = formatAlertCount(alertCount);
  const logo = renderLogo(config.logo, config.appName) || (config.logoUrl ? <img src={config.logoUrl} alt={config.appName} /> : null);

  useEffect(() => {
    if (!profileOpen) return;

    const closeProfileMenu = (event: MouseEvent) => {
      if (!profileRef.current?.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setProfileOpen(false);
    };

    document.addEventListener("mousedown", closeProfileMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeProfileMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [profileOpen]);

  return (
    <header className="in-app-top-nav">
      <div className="in-app-top-left">
        <button
          type="button"
          className="in-app-menu-button"
          onClick={onToggleNav}
          aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={expanded}
        >
          <Icon name="menu" />
        </button>

        <button
          type="button"
          className="in-app-logo-block"
          onClick={() => config.homeRoute && onNavigate(config.homeRoute)}
          aria-label={config.appName}
          disabled={!config.homeRoute}
        >
          {logo || <span>{config.logoText || config.appName}</span>}
        </button>
      </div>

      <label className="in-app-search" aria-label="Search">
        <input
          type="search"
          placeholder={config.searchPlaceholder || "Search"}
          value={config.searchValue}
          onChange={(event) => onSearch?.(event.target.value)}
          disabled={!onSearch}
        />
        <Icon name="search" className="in-app-search-icon" />
      </label>

      <div className="in-app-top-actions">
        <div className="in-app-grid-wrap">
          <button
            type="button"
            className="in-app-grid-button"
            aria-label="Open main dashboard"
            aria-haspopup="true"
            onClick={() => onNavigate(config.mainDashboardRoute)}
          >
            <Icon name="apps" />
          </button>
          <AppSwitcher
            items={config.switcherItems}
            mainDashboardRoute={config.mainDashboardRoute}
            onNavigate={onNavigate}
          />
        </div>

        <button
          type="button"
          className="in-app-icon-button in-app-alert-icon-button"
          aria-label={alertBadge ? `${alertBadge} alerts` : "Notifications"}
          onClick={onAlertsClick}
        >
          <Icon name="notifications_active" />
          {alertBadge ? <span className="in-app-alert-badge">{alertBadge}</span> : null}
        </button>

        <button type="button" className="in-app-alert-link" onClick={onAlertsClick}>
          ALERTS
        </button>

        <div className="in-app-profile-wrap" ref={profileRef}>
          <button
            type="button"
            className="in-app-profile"
            onClick={() => setProfileOpen((value) => !value)}
            aria-haspopup="menu"
            aria-expanded={profileOpen}
          >
            {account.customTrigger || (
              <>
                {account.avatar || (account.avatarUrl ? (
                  <img className="in-app-profile-avatar" src={account.avatarUrl} alt="" />
                ) : (
                  <Icon name="account_circle" className="in-app-profile-icon" />
                ))}
                <span className="in-app-profile-copy">
                  <span className="in-app-profile-name">{account.displayName || account.email}</span>
                  {account.roleLabel ? <span className="in-app-profile-role">{account.roleLabel}</span> : null}
                </span>
              </>
            )}
          </button>
          <div className="in-app-profile-menu" role="menu" hidden={!profileOpen}>
            {account.customMenu ||
              menuItems.map((item) => (
                <AccountMenuItem
                  key={item.id}
                  item={item}
                  onDone={() => setProfileOpen(false)}
                  onNavigate={onNavigate}
                />
              ))}
          </div>
        </div>
      </div>
    </header>
  );
}
